import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { UI_CONSTANTS } from '@petmas/shared';

export function Header() {
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [cartBounce, setCartBounce] = useState(false);
  const navigate = useNavigate();
  const { totalItems, openCart } = useCart();
  const { customer } = useAuth();
  const prevTotalItems = useRef(totalItems);

  // Trigger bounce animation when cart items change
  useEffect(() => {
    if (totalItems > prevTotalItems.current) {
      setCartBounce(true);
      const timer = setTimeout(() => setCartBounce(false), UI_CONSTANTS.DEBOUNCE_DELAY_MS);
      return () => clearTimeout(timer);
    }
    prevTotalItems.current = totalItems;
  }, [totalItems]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/buscar?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-white shadow-sm">
      {/* Main header - White background with logo, search, icons */}
      <div className="container py-4">
        <div className="flex items-center justify-between gap-4">
          {/* Logo - Pet (brown) Mas (green) */}
          <Link to="/" className="flex-shrink-0 group">
            <span className="font-display text-3xl font-bold">
              <span className="text-secondary">Pet</span>
              <span className="text-primary">Mas</span>
            </span>
          </Link>

          {/* Search bar - Green border, rounded */}
          <form onSubmit={handleSearch} className="flex-1 max-w-md hidden md:flex">
            <div className="relative w-full">
              <input
                type="text"
                placeholder="Buscar productos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full border-2 border-primary rounded-full py-2.5 px-5 pr-12 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
              <button
                type="submit"
                className="absolute right-0 top-0 h-full px-4 bg-primary text-white rounded-r-full hover:bg-primary-dark transition-colors"
              >
                <SearchIcon className="w-5 h-5" />
              </button>
            </div>
          </form>

          {/* Actions - User and Cart */}
          <div className="flex items-center gap-3">
            {/* Mobile search toggle */}
            <button
              className="md:hidden p-2 text-secondary hover:text-primary transition-colors"
              aria-label="Buscar"
            >
              <SearchIcon className="w-5 h-5" />
            </button>

            {/* Account */}
            <Link
              to={customer ? '/cuenta' : '/login'}
              className="hidden sm:flex items-center gap-2 text-secondary hover:text-primary transition-colors"
            >
              <UserIcon className="w-5 h-5" />
              <span className="text-sm font-medium">
                {customer ? 'Mi Cuenta' : 'Iniciar Sesión'}
              </span>
            </Link>

            {/* Cart with bounce animation */}
            <button
              onClick={openCart}
              className="relative p-2 text-secondary hover:text-primary transition-colors"
              aria-label={`Carrito (${totalItems} items)`}
            >
              <CartIcon className="w-6 h-6" />
              {totalItems > 0 && (
                <span
                  className={`absolute -top-1 -right-1 bg-primary text-white text-xs font-bold rounded-full min-w-[20px] h-[20px] flex items-center justify-center px-1 ${
                    cartBounce ? 'animate-cart-bounce' : ''
                  }`}
                >
                  {totalItems > UI_CONSTANTS.MAX_CART_DISPLAY ? `${UI_CONSTANTS.MAX_CART_DISPLAY}+` : totalItems}
                </span>
              )}
            </button>

            {/* Mobile menu toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-secondary hover:text-primary transition-colors"
              aria-label="Menu"
            >
              {isMobileMenuOpen ? (
                <CloseIcon className="w-6 h-6" />
              ) : (
                <MenuIcon className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile search */}
        <form onSubmit={handleSearch} className="mt-4 md:hidden">
          <div className="relative">
            <input
              type="text"
              placeholder="Buscar productos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full border-2 border-primary rounded-full py-2.5 px-5 pr-12 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
            <button
              type="submit"
              className="absolute right-0 top-0 h-full px-4 bg-primary text-white rounded-r-full hover:bg-primary-dark transition-colors"
            >
              <SearchIcon className="w-5 h-5" />
            </button>
          </div>
        </form>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-primary">
          <nav className="container py-4 space-y-1">
            <MobileNavLink to="/" onClick={() => setIsMobileMenuOpen(false)}>
              Inicio
            </MobileNavLink>
            <MobileNavLink to="/perros" onClick={() => setIsMobileMenuOpen(false)}>
              Perros
            </MobileNavLink>
            <MobileNavLink to="/gatos" onClick={() => setIsMobileMenuOpen(false)}>
              Gatos
            </MobileNavLink>
            <MobileNavLink to="/prendas" onClick={() => setIsMobileMenuOpen(false)}>
              Prendas
            </MobileNavLink>
            <MobileNavLink to="/placas" onClick={() => setIsMobileMenuOpen(false)}>
              Placas
            </MobileNavLink>
            <MobileNavLink to="/farmacia" onClick={() => setIsMobileMenuOpen(false)}>
              Farmacia
            </MobileNavLink>
            <MobileNavLink to="/outlet" onClick={() => setIsMobileMenuOpen(false)}>
              Outlet
            </MobileNavLink>
            <MobileNavLink to="/blog" onClick={() => setIsMobileMenuOpen(false)}>
              Blog
            </MobileNavLink>
            <div className="pt-2 mt-2 border-t border-white/20">
              <MobileNavLink to={customer ? '/cuenta' : '/login'} onClick={() => setIsMobileMenuOpen(false)}>
                {customer ? 'Mi Cuenta' : 'Iniciar Sesión'}
              </MobileNavLink>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}

function MobileNavLink({
  to,
  children,
  onClick,
}: {
  to: string;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="block py-2 px-4 text-white hover:bg-white/10 rounded transition-colors font-medium"
    >
      {children}
    </Link>
  );
}

// Icons
function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  );
}

function UserIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
      />
    </svg>
  );
}

function CartIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
  );
}

function MenuIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 6h16M4 12h16M4 18h16"
      />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}
